<script setup>
import { ref, computed, watch } from 'vue'
import moment from 'moment'

const props = defineProps({
  modelValue: {
    type: String,
    default: null
  },
  availableDates: {
    type: Array,
    default: () => []
  },
  density: {
    type: String,
    default: 'default'
  }
})

const emit = defineEmits(['update:modelValue', 'submit-request'])

const isOpen = ref(false)
const currentMonth = ref(moment())
const selectedDate = ref(props.modelValue)

// Watch for external changes to modelValue
watch(() => props.modelValue, newVal => {
  selectedDate.value = newVal
  if (newVal) {
    currentMonth.value = moment(newVal)
  }
})

// Check if viewing current month (to hide back button)
const isCurrentMonth = computed(() => {
  return currentMonth.value.isSame(moment(), 'month')
})

// Check if there are available dates in any month after the current view
const hasNextMonthDates = computed(() => {
  const endOfCurrentView = moment(currentMonth.value).endOf('month')
  
  return props.availableDates.some(date => moment(date).isAfter(endOfCurrentView, 'day'))
})

// Month navigation
const previousMonth = () => {
  currentMonth.value = moment(currentMonth.value).subtract(1, 'month')
}

const nextMonth = () => {
  currentMonth.value = moment(currentMonth.value).add(1, 'month')
}

// Get month name and year
const monthYearDisplay = computed(() => {
  return currentMonth.value.format('MMMM YYYY')
})

// Generate calendar grid
const calendarDays = computed(() => {
  const startOfMonth = moment(currentMonth.value).startOf('month')
  const endOfMonth = moment(currentMonth.value).endOf('month')
  const startDate = moment(startOfMonth).startOf('week') // Start from Sunday
  const endDate = moment(endOfMonth).endOf('week')
  const today = moment().format('YYYY-MM-DD')

  const days = []
  let day = moment(startDate)

  while (day.isBefore(endDate) || day.isSame(endDate, 'day')) {
    const dateStr = day.format('YYYY-MM-DD')
    days.push({
      date: dateStr,
      day: day.date(),
      isCurrentMonth: day.isSame(currentMonth.value, 'month'),
      isToday: dateStr === today,
      isSelected: dateStr === selectedDate.value,
      isPast: dateStr < today,
      isAvailable: props.availableDates.includes(dateStr)
    })
    day = moment(day).add(1, 'day')
  }

  return days
})

// Select date and close immediately
const selectDate = day => {
  if (!day.isAvailable) return
  selectedDate.value = day.date
  emit('update:modelValue', day.date)
  isOpen.value = false
}

// Clear selection and close immediately
const clearSelection = () => {
  selectedDate.value = null
  emit('update:modelValue', null)
  isOpen.value = false
}

// Clear selection and emit immediately (for inline clear button)
const clearAndEmit = () => {
  selectedDate.value = null
  emit('update:modelValue', null)
}

// Open dialog
const open = () => {
  isOpen.value = true

  // Set current month to selected date or first available date
  if (selectedDate.value) {
    currentMonth.value = moment(selectedDate.value)
  } else if (props.availableDates.length > 0) {
    currentMonth.value = moment(props.availableDates[0])
  }
}

// Format date for button display
const formattedDate = computed(() => {
  if (!selectedDate.value) return 'All Dates'

  return moment(selectedDate.value).format('ddd, MMM D')
})

// Expose open method
defineExpose({ open })
</script>

<template>
  <div class="date-picker-wrapper">
    <!-- Trigger Button -->
    <VBtn
      variant="outlined"
      color="grey-darken-1"
      class="date-picker-trigger"
      :density="density"
      @click="open"
    >
      <span class="trigger-content">
        <VIcon
          icon="mdi-calendar-outline"
          class="calendar-icon"
        />
        <span class="date-text">{{ formattedDate }}</span>
        <VIcon
          v-if="selectedDate"
          icon="mdi-close-circle"
          size="24"
          class="clear-icon"
          @click.stop="clearAndEmit"
        />
      </span>
    </VBtn>

    <!-- Bottom Sheet Dialog -->
    <VDialog
      v-model="isOpen"
      max-width="500"
      transition="dialog-bottom-transition"
      :scrim="true"
    >
      <VCard class="date-picker-card">
        <!-- Header -->
        <VCardTitle class="date-picker-header">
          <div>
            <span style="font-size: 14px;">Instant Bookings - Elevated Experiences</span>
            <div class="submit-request-link">
              <span
                class="d-inline-flex align-center cursor-pointer"
                @click="emit('submit-request'); isOpen = false"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="mr-1"
                >
                  <ellipse
                    cx="8"
                    cy="8"
                    rx="8"
                    ry="8"
                    fill="var(--ds-brand-primary-dark)"
                  />
                  <path
                    d="M10.5261 7.99958H5.05243M7.78927 5.47327V10.5259"
                    stroke="white"
                    stroke-width="0.860438"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span class="submit-request-text">Submit request for other dates/restaurants</span>
              </span>
            </div>
          </div>
          <div class="header-actions">
            <VBtn
              icon
              variant="text"
              size="small"
              @click="isOpen = false"
            >
              <VIcon icon="mdi-close" />
            </VBtn>
          </div>
        </VCardTitle>

        <VCardText class="date-picker-body">
          <!-- Month Navigation -->
          <div class="month-navigation">
            <VBtn
              v-if="!isCurrentMonth"
              icon
              variant="text"
              size="small"
              @click="previousMonth"
            >
              <VIcon icon="mdi-chevron-left" />
            </VBtn>
            <div
              v-else
              style="width: 40px;"
            />
            <span class="month-year">{{ monthYearDisplay }}</span>
            <VBtn
              v-if="hasNextMonthDates"
              icon
              variant="text"
              size="small"
              @click="nextMonth"
            >
              <VIcon icon="mdi-chevron-right" />
            </VBtn>
            <div
              v-else
              style="width: 40px;"
            />
          </div>

          <!-- Day Headers -->
          <div class="calendar-grid">
            <div
              v-for="day in ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']"
              :key="day"
              class="day-header"
            >
              {{ day }}
            </div>

            <!-- Calendar Days -->
            <div
              v-for="day in calendarDays"
              :key="day.date"
              class="calendar-day"
              :class="{
                'not-current-month': !day.isCurrentMonth,
                'is-past': day.isPast,
                'is-selected': day.isSelected,
                'is-available': day.isAvailable,
                'is-unavailable': !day.isAvailable && day.isCurrentMonth
              }"
              @click="selectDate(day)"
            >
              {{ day.day }}
            </div>
          </div>
          <div class="text-center align-center">
            <VBtn
              v-if="selectedDate"
              variant="text"
              style="font-size: 14px;"
              color="primary"
              @click="clearSelection"
            >
              View All Dates
            </VBtn>
          </div>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
.date-picker-wrapper {
  display: inline-block;
  width: 100%;
}

.date-picker-trigger {
  width: 100%;
  min-width: 140px;
  text-transform: none;
  font-weight: 400;
  height: 40px !important;
  padding-left: 0px !important;
  padding-right: 0px !important;

  :deep(.v-btn__content) {
    width: 100%;
  }

  .trigger-content {
    display: flex;
    align-items: center;
    width: 100%;
    gap: var(--ds-space-2);
  }

  .calendar-icon {
    flex-shrink: 0;
  }

  .date-text {
    flex: 1;
    text-align: left;
  }

  .clear-icon {
    flex-shrink: 0;
    color: var(--ds-text-muted);
    transition: color var(--ds-duration-normal) ease;

    &:hover {
      color: #616161;
    }
  }
}

.date-picker-card {
  border-radius: var(--ds-radius-lg) var(--ds-radius-lg) 0 0;
  box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.1);
}

.date-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.submit-request-link {
  margin-top: var(--ds-space-1);
}

.submit-request-text {
  font-size: var(--ds-text-xs);
  font-weight: 400;
  color: var(--ds-brand-primary-dark);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2);
  margin-top: -12px;
  margin-right: -16px;
}

.date-picker-body {
  padding: var(--ds-space-6);
}

.month-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--ds-space-6);
}

.month-year {
  font-size: var(--ds-text-lg);
  font-weight: 500;
  color: #050000;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: var(--ds-space-2);
  margin-top: var(--ds-space-4);
}

.day-header {
  text-align: center;
  font-size: var(--ds-text-base);
  font-weight: 400;
  color: var(--ds-text-muted);
  padding: var(--ds-space-2) 0;
}

.calendar-day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ds-text-md);
  font-weight: 400;
  color: #050000;
  border-radius: var(--ds-radius-full);
  cursor: pointer;
  transition: all var(--ds-duration-normal) ease;
  user-select: none;

  &.not-current-month {
    color: transparent;
    pointer-events: none;
  }

  &.is-past {
    color: var(--ds-text-disabled);
    pointer-events: none;
  }

  &.is-unavailable {
    color: var(--ds-border-default);
    pointer-events: none;
  }

  &.is-available {
    &:hover {
      background-color: var(--ds-surface-inset);
    }
  }

  &.is-selected {
    background-color: #050000;
    color: var(--ds-text-on-dark);
    font-weight: 600;

    &:hover {
      background-color: #050000;
    }
  }
}

// Mobile responsiveness
@media (max-width: 600px) {
  .calendar-grid {
    gap: var(--ds-space-1);
  }

  .calendar-day {
    font-size: var(--ds-text-base);
  }

  .date-picker-body {
    padding: var(--ds-space-4);
  }
}
</style>
